import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { useInView } from '../../../components/hooks/useInView.jsx';
import { Activity, Apple, Moon, TrendingUp } from 'lucide-react';

export function FeaturesSection() {
  const [ref, isInView] = useInView({ threshold: 0.2 });
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const features = [
    {
      icon: Activity,
      title: 'Personalized Workouts',
      description: 'Custom workout plans tailored to your fitness level, goals, and preferences. From beginners to advanced athletes.'
    },
    {
      icon: Apple,
      title: 'Meal Suggestions',
      description: 'Smart meal recommendations based on your dietary preferences, nutritional goals, and lifestyle requirements.'
    },
    {
      icon: Moon,
      title: 'Sleep Tracking',
      description: 'Comprehensive sleep monitoring to help you understand and improve your rest patterns for optimal recovery.'
    },
    {
      icon: TrendingUp,
      title: 'Progress Visualization',
      description: 'Beautiful charts and insights that make it easy to track your journey and celebrate your achievements.'
    }
  ];

  return (
    <section ref={containerRef} className="min-h-screen flex items-center py-20 px-6 bg-gradient-to-b from-transparent via-black/30 to-transparent">
      <div ref={ref} className="max-w-7xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl mb-8">
            Core Features
          </h2>
          <div className="h-1 w-20 bg-[#fd5b38] mx-auto mb-8" />
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Everything you need for a complete wellness experience in one place
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const scale = useTransform(
              scrollYProgress,
              [0.2 + index * 0.05, 0.4 + index * 0.05],
              [0.8, 1]
            );
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const opacity = useTransform(
              scrollYProgress,
              [0.2 + index * 0.05, 0.4 + index * 0.05],
              [0, 1]
            );

            return (
              <motion.div
                key={index}
                style={{ scale, opacity }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="bg-gradient-to-br from-[#1a1a1a] to-black p-8 rounded-3xl item-border border-gray-800 hover:border-[#fd5b38] transition-all duration-300 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#fd5b38]/0 to-[#fd5b38]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#fd5b38]/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#fd5b38]/30 transition-colors duration-300">
                    <feature.icon className="w-8 h-8 text-[#fd5b38]" />
                  </div>
                  
                  <h3 className="text-2xl mb-4">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>

                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#fd5b38]/5 rounded-full blur-3xl group-hover:bg-[#fd5b38]/10 transition-all duration-300" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
